import { UnsupportedApiError } from '../api/contracts';
import { api } from "../api";
import type { ApiResponse } from "../api";

// Types based on backend DTOs
export interface LoginCredentials {
  tenantSlug: string;
  username: string;
  password: string;
  twoFactorCode?: string;
}

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  role: string;
  roleId: string;
  branch?: string;
  branchId?: string;
  permissions: string[];
  tenantId: string;
  tenantSlug: string;
  tenantStatus: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  expiresIn: number;
  requiresTwoFactor?: boolean;
  twoFactorMethod?: "sms" | "app" | "email" | undefined;
}

export interface WorkspaceSignupData {
  businessName: string;
  workspaceSlug?: string;
  username: string;
  email: string;
  phone?: string;
  password: string;
  timezone?: string;
  locale?: string;
  defaultCurrency?: string;
}

export interface WorkspaceSignupResponse extends LoginResponse {
  workspace: {
    id: string;
    name: string;
    slug: string;
    status: string;
    trialEndsAt: string;
  };
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresTwoFactor: boolean;
  twoFactorMethod?: "sms" | "app" | "email";
  login: (credentials: LoginCredentials) => Promise<void>;
  verifyTwoFactorCode: (code: string) => Promise<void>;
  sendTwoFactorCode: (method: "sms" | "email") => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Auth API service
export const authApi = {
  async signup(data: WorkspaceSignupData): Promise<WorkspaceSignupResponse> {
    const response = await api.post<ApiResponse<WorkspaceSignupResponse>>(
      "/onboarding/signup",
      data,
    );
    return response.data.data;
  },

  async checkWorkspaceSlug(
    slug: string,
  ): Promise<{ slug: string; available: boolean }> {
    const response = await api.get<
      ApiResponse<{ slug: string; available: boolean }>
    >(`/onboarding/check-slug/${encodeURIComponent(slug)}`);
    return response.data.data;
  },

  /**
   * تسجيل الدخول
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      "/auth/login",
      credentials,
    );
    return response.data.data;
  },

  /**
   * تسجيل حساب جديد
   */
  /**
   * تسجيل الخروج
   */
  async logout(): Promise<{ message: string }> {
    const response =
      await api.post<ApiResponse<{ message: string }>>("/auth/logout");
    return response.data.data;
  },

  /**
   * تحديث الرمز المميز
   */
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await api.post<ApiResponse<RefreshTokenResponse>>(
      "/auth/refresh",
      {
        refreshToken,
      },
    );
    return response.data.data;
  },

  /**
   * الحصول على معلومات المستخدم الحالي
   */
  async getCurrentUser(): Promise<AuthUser> {
    const response = await api.get<ApiResponse<AuthUser>>("/auth/me");
    return response.data.data;
  },

  /**
   * التحقق من صحة الرمز المميز
   */
  async verifyToken(): Promise<{ valid: boolean; user: AuthUser }> {
    const response =
      await api.get<ApiResponse<{ valid: boolean; user: AuthUser }>>(
        "/auth/verify",
      );
    return response.data.data;
  },

  /**
   * إرسال رمز 2FA
   */
  async sendTwoFactorCode(
    method: "sms" | "email",
  ): Promise<{ message: string }> { void method; throw new UnsupportedApiError('sendTwoFactorCode'); },

  /**
   * التحقق من رمز 2FA
   */
  async verifyTwoFactorCode(code: string): Promise<LoginResponse> { void code; throw new UnsupportedApiError('verifyTwoFactorCode'); },

  /**
   * تفعيل/إلغاء تفعيل 2FA
   */
  async toggleTwoFactor(
    enabled: boolean,
    method?: "sms" | "app" | "email",
  ): Promise<{ message: string }> { void enabled; void method; throw new UnsupportedApiError('toggleTwoFactor'); },

  /**
   * إعداد 2FA بالتطبيق
   */
  async setupTwoFactorApp(): Promise<{ secret: string; qrCodeUrl: string }> { throw new UnsupportedApiError('setupTwoFactorApp'); },

  /**
   * تغيير كلمة المرور
   */
  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await api.patch<ApiResponse<{ message: string }>>(
      "/auth/password",
      data,
    );
    return response.data.data;
  },
};

export default authApi;
